/**
 * Email verification (PRD §6.2).
 *
 * Serves two arrivals:
 *  - Following the link from the email (`?token=…`) — verify immediately.
 *  - Landing here because the session is valid but unverified — explain and
 *    offer to resend.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/auth-layout';
import {
  DarkAlert,
  DarkField,
  DarkInput,
  DarkSubmit,
  FieldIcons,
  ModuleBanner,
  Spinner,
} from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useAuthRequest } from '@/hooks/use-auth-request';
import { ApiError, api } from '@/lib/api';

type Phase = 'verifying' | 'verified' | 'failed' | 'prompt';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const authRequest = useAuthRequest();

  const [phase, setPhase] = useState<Phase>(token ? 'verifying' : 'prompt');
  const [error, setError] = useState<string | null>(null);
  const [awaitingOrganization, setAwaitingOrganization] = useState(false);

  // React 18+ mounts effects twice in development; verifying is single-use, so
  // a second call would report a spurious "link already used".
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        const result = await api.verifyEmail(token);
        setAwaitingOrganization(result.awaitingOrganization);
        setPhase('verified');
        // The session (if any) now reflects a verified address.
        await refresh().catch(() => undefined);
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'We could not verify this link. Please request a new one.',
        );
        setPhase('failed');
      }
    })();
  }, [token, refresh]);

  if (phase === 'verifying') {
    return (
      <AuthLayout title="Verifying your email">
        <div className="flex items-center gap-3 text-sm text-slate-400" role="status">
          <Spinner className="size-5 text-brand-600" />
          Checking your verification link…
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'verified') {
    return (
      <AuthLayout
        title="Email verified"
        description="Your address is confirmed."
        footer={
          <Link
            to={authRequest.link('/sign-in')}
            className="font-medium text-brand-400 hover:text-brand-300"
          >
            Back to sign in
          </Link>
        }
      >
        <div className="space-y-5">
          <DarkAlert tone="success">
            Thanks — your email address has been verified.
          </DarkAlert>

          {awaitingOrganization ? (
            <>
              <p className="text-sm text-slate-400">
                We could not match your address to an organization automatically, so a MindPeer
                administrator needs to assign you before you can open a module.
              </p>
              <DarkSubmit
                type="button"
                onClick={() => navigate(authRequest.link('/awaiting-approval'))}
              >
                What happens next?
              </DarkSubmit>
            </>
          ) : (
            <DarkSubmit
              type="button"
              onClick={() => {
                // A verified user mid-authorization can go straight into the
                // module; only send them to the portal when there is no flow.
                if (user && authRequest.resume()) return;
                navigate(user ? '/account' : authRequest.link('/sign-in'));
              }}
            >
              {authRequest.applicationName
                ? `Continue to ${authRequest.applicationName}`
                : user
                  ? 'Go to your account'
                  : 'Sign in'}
            </DarkSubmit>
          )}
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'failed') {
    return (
      <AuthLayout title="This link didn't work" description={error ?? undefined}>
        <ResendForm defaultEmail={user?.email ?? ''} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Verify your email"
      description={
        user
          ? `We sent a verification link to ${user.email}. Open it to finish setting up your account.`
          : 'Open the verification link we emailed you to finish setting up your account.'
      }
      footer={
        <Link
          to={authRequest.link('/sign-in')}
          className="font-medium text-brand-400 hover:text-brand-300"
        >
          Back to sign in
        </Link>
      }
    >
      {/*
        Shown while the user is waiting on the email: a reminder of where they
        were headed, so an interrupted module sign-in still makes sense when they
        come back to this tab later.
      */}
      <ModuleBanner applicationName={authRequest.applicationName} />

      <ResendForm defaultEmail={user?.email ?? ''} />
    </AuthLayout>
  );
}

/** Request a fresh verification link. */
function ResendForm({ defaultEmail }: { defaultEmail: string }) {
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setSending(true);
      setError(null);
      void (async () => {
        try {
          await api.resendVerification(email);
          setSent(true);
        } catch (caught) {
          setError(caught instanceof ApiError ? caught.message : 'Please try again.');
        } finally {
          setSending(false);
        }
      })();
    },
    [email],
  );

  // Deliberately non-committal: confirming which addresses need verification
  // would leak account existence (PRD §16).
  if (sent) {
    return (
      <DarkAlert tone="success">
        If that address needs verification, a new link is on its way.
      </DarkAlert>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {error && <DarkAlert tone="error">{error}</DarkAlert>}

      <DarkField label="Email" htmlFor="resend-email">
        <DarkInput
          id="resend-email"
          name="email"
          type="email"
          icon={FieldIcons.mail}
          placeholder="you@company.com"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </DarkField>

      <DarkSubmit loading={sending}>
        Send a new link
      </DarkSubmit>
    </form>
  );
}
